import { FunnelCart } from 'src/ts/types/cart';
import {
  DiscountCode,
  getDiscountCodeClientSide,
} from 'src/services/rails-service';
import { UserContextInterface } from 'src/context/user';
import logger from 'logger';
import { verifyUserDiscountEligibility } from 'src/services/cart-order/discounts/v3/discounts.client';

/**
 * This function really only exists so the useEffect that pulls discount codes doesn't
 * attempt to pull a full discount code if the code hasn't changed on the cart.
 */
export function getUnverifiedCartDiscountCode(
  cart: FunnelCart,
): string | undefined {
  const verifiedDiscountCode = cart.discount?.code;
  const unverifiedDiscountCode = cart.funnelData['params']['discountCode'];

  return verifiedDiscountCode ?? unverifiedDiscountCode;
}

/**
 * There are multiple ways to pull discounts, which have precedence, and different
 * validation requirements.
 *
 * Precendence source:
 *  1) Discount on Cart
 *  2) Query Param
 *  3) Standing Tenant Discount Code from Tenant Config
 *
 * Of these sources, the front end DOES NOT validate the cart-applied discounts. The
 * cart-supplied discounts should encompass both previously validated discounts (that
 * will get re-validated on checkout) and account-applied discounts.
 *
 * The other two sources (query param, standing tenant discount) can be validated, but
 * only if the user is logged in. If the user is not logged in, these discounts are
 * assumed valid and are treated as "preview discount codes". These preview discounts
 * are in-memory only, they're not committed to the cart until the user is logged in
 * or on the checkout page.
 */
export async function getValidatedDiscount(input: {
  cart: FunnelCart;
  userContext: UserContextInterface | null;
}): Promise<DiscountCode | undefined> {
  const { cart, userContext } = input;

  if (!userContext) return undefined;

  const verifiedDiscountCode = cart.discount?.code;
  const unverifiedDiscountCode = cart.funnelData['params']['discountCode'];

  // If you have no discount code at this point, just bail
  if (!verifiedDiscountCode && !unverifiedDiscountCode) return undefined;

  let appliedDiscountedCode: string;
  if (verifiedDiscountCode) {
    // If you have a previously verified discount, just use that
    appliedDiscountedCode = verifiedDiscountCode;
  } else {
    // Otherwise, see if you need to validate this discount code or not
    const isLoggedIn = !!userContext.user;

    if (isLoggedIn) {
      const { eligible } = await verifyUserDiscountEligibility(
        unverifiedDiscountCode,
      );

      if (!eligible) return undefined;
    }

    appliedDiscountedCode = unverifiedDiscountCode!;
  }

  try {
    const discountCode = await getDiscountCodeClientSide({
      discountCode: appliedDiscountedCode.trim().toUpperCase(),
      tenantId: userContext.tenantId,
    });

    return discountCode;
  } catch (error) {
    logger.error({
      msg: 'FailedToFetchDiscountCode',
      extra: {
        context: {
          error,
          verifiedDiscountCode,
          unverifiedDiscountCode,
        },
      },
    });
  }
}
